import type { ListingDetails } from 'app/types/listingDetails.type';
import sanitizer from 'sanitizer';

// States that require "Fees may apply" disclosure
const FEES_DISCLOSURE_STATES = ['MA', 'CT'];

/**
 * Determines if a listing is in a state that requires fees disclosure.
 *
 * @param listing - The listing object to evaluate
 * @returns boolean - true if listing is in a state requiring fees disclosure
 */
export const requiresFeesDisclosure = (listing: ListingDetails): boolean => {
  const listingState = listing?.address?.state?.toUpperCase();
  return Boolean(listingState && FEES_DISCLOSURE_STATES.includes(listingState));
};

/**
 * Determines whether to show Total Monthly Price badge for a given listing.
 *
 * @param listing - The listing object to evaluate
 * @returns boolean - true if badge should be displayed, false otherwise
 */
export const shouldShowTotalMonthlyPriceBadge = (listing: ListingDetails): boolean => {
  // Must have valid listing object
  if (!listing) {
    return false;
  }

  // Show badge if listing has total monthly price OR if it's in a state requiring fees disclosure
  const hasTotalMonthlyPrice = Boolean(listing.hasTotalMonthlyPrice);

  return hasTotalMonthlyPrice || requiresFeesDisclosure(listing);
};

/**
 * Processes API text content for display, handling both HTML and plain text with line breaks.
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting.
 *
 * @param textContent - The text content from the API to process (can be null/undefined)
 * @returns Object with processing information for components to use
 *
 * Features:
 * - Handles null/undefined/empty string inputs safely
 * - Detects HTML content using regex pattern
 * - Sanitizes HTML content to prevent XSS attacks (removes dangerous tags/attributes)
 * - Returns info for safe HTML rendering with dangerouslySetInnerHTML
 * - Indicates when line breaks (\n) need preservation with whiteSpace: 'pre-wrap'
 * - Preserves legitimate HTML formatting like <br>, <strong>, <em>
 */
export const processApiTextContent = (textContent: string | null | undefined) => {
  // Handle null, undefined, or empty string cases
  if (!textContent) {
    return {
      isHtml: false,
      content: '',
    };
  }

  // Detect proper HTML tags in content (more precise regex)
  // Matches opening tags, closing tags, and self-closing tags, but not arbitrary < > text
  const isHtml = /<\/?[a-zA-Z][a-zA-Z0-9]*[^<>]*>/g.test(textContent);

  // Sanitize HTML content to prevent XSS attacks while preserving safe formatting
  const sanitizedContent = isHtml ? sanitizer.sanitize(textContent) : textContent;

  return {
    isHtml,
    content: sanitizedContent,
  };
};
